import * as React from 'react'
import { Project, ProjectColumnConfig, ProjectColumnProperty, ProjectColumns, ProjectProperty, ResourceRendererOptions, ResourceRendererResult, Settings } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import Badge from '../Badge/Badge'
import styled from 'styled-components'
import ResourceTableColor from './ResourceTableColor'
import showdown from '../../utilities/showdown'
import ProjectHelper from '../../helpers/ProjectHelper'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'

const Tags = styled.div`
  display: flex;
  flex-direction: row; 
  gap: 4px;
`

export const DEFAULT_PROJECT_COLUMNS: ProjectColumnProperty[] = [
  'name',
  'code',
  'tags',
  'contact',
  'status',
  'start_date',
  'end_date'
]

const ProjectColumnConfigs: Record<ProjectColumnProperty, ProjectColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('Project::id') }),
    render: ({ project }) => project.id || '-'
  },
  name: {
    header: () => ({ title: i18n.t('Project::name') }),
    render: ({ project }) => project.name || '-',
  },
  code: {
    header: () => ({ title: i18n.t('Project::code') }),
    render: ({ project }) => project.code || '-'
  },
  po_number: {
    header: () => ({ title: i18n.t('Project::po_number') }),
    render: ({ project }) => project.po_number || '-'
  },
  contact: {
    header: () => ({ title: i18n.t('Project::contact') }),
    render: ({ project }) => project.contact?.name || '-'
  },
  color: {
    header: () => ({ title: i18n.t('Project::color') }),
    render: ({ project }) => <ResourceTableColor style={{ backgroundColor: project.color || '#ccc' }} />
  },
  notes: {
    header: () => ({ title: i18n.t('Project::notes') }),
    render: ({ project }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(project.notes) }} />
  },
  billable_type: {
    header: () => ({ title: i18n.t('Project::billable_type') }),
    render: ({ project }) => ProjectHelper.getProjectBillableTypeLabel(project.billable_type)
  },
  labour_costs: {
    header: () => ({ title: i18n.t('Project::labour_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.labour_costs)
  },
  budget_labour_costs: {
    header: () => ({ title: i18n.t('Project::budget_labour_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget_labour_costs)
  },
  expense_costs: {
    header: () => ({ title: i18n.t('Project::expense_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.expense_costs)
  },
  budget_expense_costs: {
    header: () => ({ title: i18n.t('Project::budget_expense_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget_expense_costs)
  },
  total_costs: {
    header: () => ({ title: i18n.t('Project::total_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.total_costs)
  },
  budget_total_costs: {
    header: () => ({ title: i18n.t('Project::budget_total_costs') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget_total_costs)
  },
  billable_hours: {
    header: () => ({ title: i18n.t('Project::billable_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.billable_hours)
  },
  budget_billable_hours: {
    header: () => ({ title: i18n.t('Project::budget_billable_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.budget_billable_hours)
  },
  non_billable_hours: {
    header: () => ({ title: i18n.t('Project::non_billable_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.non_billable_hours)
  },
  budget_non_billable_hours: {
    header: () => ({ title: i18n.t('Project::budget_non_billable_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.budget_non_billable_hours)
  },
  total_hours: {
    header: () => ({ title: i18n.t('Project::total_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.total_hours)
  },
  budget_total_hours: {
    header: () => ({ title: i18n.t('Project::budget_total_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.budget_total_hours)
  },
  budget_type: {
    header: () => ({ title: i18n.t('Project::budget_type') }),
    render: ({ project }) => ProjectHelper.getProjectBudgetTypeLabel(project.budget_type) || '-'
  },
  budget: {
    header: () => ({ title: i18n.t('Project::budget') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget) || '-'
  },
  budget_hours: {
    header: () => ({ title: i18n.t('Project::budget_hours') }),
    render: ({ project, setting }) => NumberFormatter.formatNumber(setting.number_format, project.budget_hours) || '-'
  },
  budget_alert_treshold: {
    header: () => ({ title: i18n.t('Project::budget_alert_treshold') }),
    render: ({ project }) => project.budget_alert_treshold ? <Badge type='grey'>{`${project.budget_alert_treshold * 100}%`}</Badge> : '-'
  },
  budget_spent: {
    header: () => ({ title: i18n.t('Project::budget_spent') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget_spent) || '-'
  },
  budget_remaining: {
    header: () => ({ title: i18n.t('Project::budget_remaining') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.budget_remaining) || '-'
  },
  hourly_rate: {
    header: () => ({ title: i18n.t('Project::hourly_rate') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.hourly_rate) || '-'
  },
  day_rate: {
    header: () => ({ title: i18n.t('Project::day_rate') }),
    render: ({ project, setting }) => NumberFormatter.formatCurrency(setting.default_currency, setting.number_format, project.day_rate) || '-'
  },
  estimated_start_date: {
    header: () => ({ title: i18n.t('Project::estimated_start_date') }),
    render: ({ project, setting }) => project.estimated_start_date ? moment(project.estimated_start_date).format(setting.date_format) : '-'
  },
  estimated_end_date: {
    header: () => ({ title: i18n.t('Project::estimated_end_date') }),
    render: ({ project, setting }) => project.estimated_end_date ? moment(project.estimated_end_date).format(setting.date_format) : '-'
  },
  start_date: {
    header: () => ({ title: i18n.t('Project::start_date') }),
    render: ({ project, setting }) => project.start_date ? moment(project.start_date).format(setting.date_format) : '-'
  },
  end_date: {
    header: () => ({ title: i18n.t('Project::end_date') }),
    render: ({ project, setting }) => project.end_date ? moment(project.end_date).format(setting.date_format) : '-'
  },
  cancel_date: {
    header: () => ({ title: i18n.t('Project::cancel_date') }),
    render: ({ project, setting }) => project.cancel_date ? moment(project.cancel_date).format(setting.date_format) : '-'
  },
  status: {
    header: () => ({ title: i18n.t('Project::status') }),
    render: ({ project }) => ProjectHelper.getStatusTitle(project.status) || '-'
  },
  archived: {
    header: () => ({ title: i18n.t('Project::archived') }),
    render: ({ project }) => project.archived ? i18n.t('Project::Yes') : i18n.t('Project::No')
  },
  rate_type: {
    header: () => ({ title: i18n.t('Project::rate_type') }),
    render: ({ project }) => ProjectHelper.getRateTypeLabel(project.rate_type) || '-'
  },
  tags: {
    header: () => ({ title: i18n.t('Project::tags') }),
    render: ({ project }) => {
      if (project.tags && project.tags.length > 0) {
        return <Tags>{project?.tags?.map(tag => <Badge type='grey' key={tag.id}>{tag.name}</Badge>)}</Tags>
      }
      return '-'
    }
  },
  created_at: {
    header: () => ({ title: i18n.t('Project::created_at') }),
    render: ({ project, setting }) => project.created_at ? moment(project.created_at).format(setting.date_format) : '-'
  },
}

export function getProjectTableRenderer(options: ResourceRendererOptions<Project>): ResourceRendererResult<Project, ProjectProperty> {
  const {
    setting,
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = ProjectColumns.map(column => ({ label: i18n.t(`Project::${column}`), value: column }))
  const selectedColumns: ProjectProperty[] = LocalStorage
    .get(LocalStorageKey.PROJECT_COLUMNS, DEFAULT_PROJECT_COLUMNS)
    .filter((column) => ProjectColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...ProjectColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (project: Project, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(project.id)
    let actions: IActionListItem[] = [
      { key: 'edit', icon: 'edit-solid', content: i18n.t('ProjectsTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('ProjectsTable::Delete'), destructive: true }
    ]

    const applyMaxWidthColumns: ProjectProperty[] = [
      'name',
      'notes',
      'contact'
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${project.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(project) : undefined}
        maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
        ellipse={applyMaxWidthColumns.includes(column)}
        textAlign={ProjectColumnConfigs[column].header().align}
      >
        {ProjectColumnConfigs[column].render({ project, ...options })}
      </ResourceTableRowData>
    ))

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${project.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, project)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={project.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, project.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getProjectTableRenderer 
